// Convert difficulty_type from its Mitsuru number to corresponding string value
export const difficultyTypeMitsuruToString = {
    '0' : 'novice',
    '1' : 'advanced',  
    '2' : 'exhaust',
    '3' : 'infinite',
    '4' : 'maximum',
};

// Convert difficulty_type from its string value to corresponding Mitsuru number
export const difficultyTypeToMitsuru = {
    'novice' : '0',
    'advanced' : '1',
    'exhaust' : '2',
    'infinite' : '3',
    'maximum' : '4',
};

// Convert difficulty_type from its string value to corresponding number
export const difficultyTypeToNumber = {
    'novice': '1',
    'advanced': '2',
    'exhaust': '3',
    'infinite': '4',
    'maximum': '5',
};
  
// Get the corresponding acronym for the difficultyType
export const difficultyTypeToAcronym = (difficultyType, infVer) => {
    if (difficultyType === 'infinite') {
        switch (infVer) {
            case 2: return 'INF';
            case 3: return 'GRV';
            case 4: return 'HVN';
            case 5: return 'VVD';
            case 6: return 'XCD';
            default: return 'INF';
        }
        } else {
        switch (difficultyType) {
            case 'novice': return 'NOV';
            case 'advanced': return 'ADV';
            case 'exhaust': return 'EXH';
            case 'maximum': return 'MXM';
            case 'ultimate': return 'ULT';
            default: return '';
        }
    }
};
  
// List of colors to represent each actual type of difficulty
export const difficultyTypeToColor = {
    'NOV': 'purple',
    'ADV': 'yellow',
    'EXH': 'red',
    'MXM': 'powderblue',
    'INF': 'fuchsia',
    'GRV': 'orange',
    'HVN': 'skyblue',
    'VVD': 'pink',
    'XCD': 'dodgerblue',
    'ULT': '#ff4444',
};

// Clear types as the game numbers them. SV7 inserted MX CLEAR at 4, which
// pushed UC and PUC up by one — comparing against a bare 5 or 6 is exactly how
// that shift got missed once already, so compare against these instead.
export const CLEAR = {
    NO_PLAY: 0,
    FAILED: 1,
    CLEAR: 2,
    HARD: 3,
    MXC: 4,
    UC: 5,
    PUC: 6,
};

// Determine border color based on clear and grade values
export const getBorderColor = (clear, grade) => {
    if (clear <= CLEAR.FAILED) {
      return 'salmon'; // Failed / No Play
    } else if (clear === CLEAR.MXC) {
      // A real colour, not a sentinel: this value gets interpolated into
      // gradients, where an unparseable string voids the whole declaration.
      return 'white';
    } else if (clear >= CLEAR.CLEAR && clear <= CLEAR.HARD) {
      if (grade <= 7) {
        return 'lightgreen'; // Clear / HC
      } else if (grade === 8) {
        return 'rgba(50, 193, 226, 0.25)'; // AAA
      } else if (grade === 9) {
        return 'rgba(148, 251, 255, 0.55)'; // AAA+
      } else if (grade === 10) {
        return 'rgba(255, 253, 145, 0.9)'; // S
      }
    } else if (clear === CLEAR.UC) {
      return 'deeppink';
    } else if (clear === CLEAR.PUC) {
      return 'rainbow-border';
    }
    return 'rgba(200, 200, 200, 0.15)'; // Default
};

// PUC is the one clear type that cannot be expressed as a colour — it is an
// animated gradient — so it comes back as a class name to apply alongside.
// Keyed on the clear value rather than on getBorderColor's return, so it does
// not depend on that function handing back a sentinel string.
export const getClearEffectClass = (clear) => {
    if (clear === CLEAR.PUC) return 'rainbow-border';
    return '';
};

export const getOverlayColor = (clear) => {
  if (clear === CLEAR.PUC) {
    return 'puc';
  }
  if (clear === null) {
    return 'notplayed'; // No score data
  }
  return 'notpuc'; // Played but not PUC
};

// Convert clear type from its number to corresponding string value
export const clearTypeToString = {
    '0': 'NP',
    '1': 'FAIL',
    '2': 'C',
    '3': 'HC',
    '4': 'MXC',
    '5': 'UC',
    '6': 'PUC',
};

// Convert grade from its number to corresponding string value
export const gradeToString = {
    '0': 'UNPLAYED',
    '1': 'D',
    '2': 'C',
    '3': 'B',
    '4': 'A',
    '5': 'A+',
    '6': 'AA',
    '7': 'AA+',
    '8': 'AAA',
    '9': 'AAA+',
    '10': 'S',
}

// Gets jacket filenames/paths for specific difficulty, and fallback for songs without difficulty-specific jackets
export const getJacketImage = (image) => {
    const baseImageID = image.imageID.toString().padStart(4, '0');
    const difficultyNumber = difficultyTypeToNumber[image.difficultyType];
    const defaultImage = `/content/jackets/jk_${baseImageID}_${difficultyNumber}_s.webp`;
    const fallbackImage = `/content/jackets/jk_${baseImageID}_1_s.webp`;
  
    return { defaultImage, fallbackImage };
  };

// Advance an image through its shared jacket and final placeholder without
// retrying the same missing URL forever (notably NOV, whose default is _1).
export const handleJacketImageError = (
  event,
  fallbackImage,
  placeholderImage = '/content/jk_placeholder.png',
) => {
  const image = event.currentTarget;
  const currentPath = new URL(image.src, window.location.href).pathname;
  const fallbackPath = new URL(fallbackImage, window.location.href).pathname;
  const placeholderPath = new URL(placeholderImage, window.location.href).pathname;

  if (currentPath !== fallbackPath && currentPath !== placeholderPath) {
    image.src = fallbackImage;
  } else if (currentPath !== placeholderPath) {
    image.src = placeholderImage;
  }
};

// Gets image filenames/paths for UI customization options
export const getUIImagePath = (key, value) => {
  switch (true) {
    case key === 'bgm':
      return `/content/ui/valgene_item/item_bgm_${value}.webp`;
    case key === 'subbg':
      return `/content/ui/valgene_item/item_bg_${value}.webp`;
    case key === 'nemsys':
      return `/content/ui/valgene_item/item_nemsys_${value}.webp`;
    case key === 'sysbg':
      return `/content/ui/valgene_item/item_sysbg_${value}.webp`;
    case /^stamp_/.test(key):
      return `/content/ui/chat_stamp/${value}.webp`;
    default:
      return '';
  }
};

// Maps UI customization keys to their corresponding labels
export const UI_labels = {
  // appeal_card: 'Appeal Card',
  bgm: 'BG Music',
  subbg: 'Subscreen BG',
  nemsys: 'Deck Skin',
  sysbg: 'System BG',
  stamp_a: 'Stamp A',
  stamp_b: 'Stamp B',
  stamp_c: 'Stamp C',
  stamp_d: 'Stamp D',
  stamp_e: 'Stamp E',
  stamp_f: 'Stamp F',
  stamp_g: 'Stamp G',
  stamp_h: 'Stamp H',
};

// ====== SV7-specific helpers ======

// SV7 difficulty type mappings (adds 'ultimate')
export const difficultyTypeMitsuruToStringSv7 = {
    '0': 'novice',
    '1': 'advanced',
    '2': 'exhaust',
    '3': 'infinite',
    '4': 'maximum',
    '5': 'ultimate',
};

export const difficultyTypeToMitsuruSv7 = {
    'novice': '0',
    'advanced': '1',
    'exhaust': '2',
    'infinite': '3',
    'maximum': '4',
    'ultimate': '5',
};

const difficultyTypeToNumberSv7 = {
    'novice': '1',
    'advanced': '2',
    'exhaust': '3',
    'infinite': '4',
    'maximum': '5',
    'ultimate': '6',
};

// Convert SV7 encoded difnum to display string (e.g., 183 -> "18.3", 170 -> "17.0")
export const sv7DifnumToDisplay = (difnum) => {
  if (difnum >= 170) {
    return (difnum / 10).toFixed(1);
  }
  return String(Math.floor(difnum / 10));
};

// Convert SV7 encoded difnum to actual decimal level (for volforce calc)
export const sv7DifnumToLevel = (difnum) => {
  return difnum / 10;
};

// SV7 volforce clear type multipliers
// 0=NP, 1=FAIL, 2=C, 3=HC, 4=MXC, 5=UC, 6=PUC
export const sv7ClearMultipliers = {
    6: 1.10,  // PUC
    5: 1.05,  // UC
    4: 1.04,  // MXC
    3: 1.02,  // HC
    2: 1.00,  // Clear
    1: 0.50,  // Failed
    0: 0.50,  // No Play
};

// SV7 jacket image helper (supports ultimate difficulty)
export const getJacketImageSv7 = (image) => {
    const baseImageID = image.imageID.toString().padStart(4, '0');
    const difficultyNumber = difficultyTypeToNumberSv7[image.difficultyType];
    const defaultImage = `/content/jackets/jk_${baseImageID}_${difficultyNumber}_s.webp`;
    const fallbackImage = `/content/jackets/jk_${baseImageID}_1_s.webp`;
    return { defaultImage, fallbackImage };
};
